import React, {useCallback} from 'react'
import styled, {useTheme} from 'styled-components'
import {Box, Image} from '@miro-site/core'
import {
  ItemContainer,
  ItemLink,
  ItemDescription,
  Container,
  Headline,
  ViewAllLink,
  Item,
} from './Highlighted'
import {IHighlighted} from '../api/interfaces'

const HERO_IMAGE =
  'https://images.ctfassets.net/qop92tnevinq/1T3F2z5K3V3nxeJfzJHFLn/b676070f4202b95e6144ab8656adc74f/website_logo_jake_3x.png'
const HERO_LINK = '/miroverse/design-sprint-jake-knapp/'

const MIROVERSE_PROFILES_IMAGE =
  'https://images.ctfassets.net/qop92tnevinq/7B4JST2nqmYadshYohiscl/0bb93ae9446a76792853b271bc200553/miroverse_banner_faces_3x.png'
const MIROVERSE_LINK = '/miroverse/'

const HeroBannerLink = styled(ItemLink)`
  margin-bottom: 0px;
`

const BoldText = styled(Box).attrs({
  as: 'span',
  $styles: {
    fontWeight: 'bold',
  },
})``

// HeroBannerV1 or HeroBannerV2 will be promoted to HeroBanner if experiment is successful
const HeroBannerV1 = (): React.ReactElement => {
  const theme = useTheme()

  return (
    <HeroBannerLink
      target="_blank"
      href={MIROVERSE_LINK}
      $mb={theme.header.highlighted.margins.afterItem}
    >
      <ItemContainer>
        <Box
          $mr={theme.header.highlighted.margins.afterImage}
          $styles={{width: theme.header.highlighted.imageWidthV1}}
        >
          <Image name="miroverse-profiles" aria-hidden={true} src={MIROVERSE_PROFILES_IMAGE} />
        </Box>
      </ItemContainer>
    </HeroBannerLink>
  )
}

const HeroBannerV2 = (): React.ReactElement => {
  const theme = useTheme()

  return (
    <ItemLink target="_blank" href={HERO_LINK} $mb={theme.header.highlighted.margins.afterItem}>
      <ItemContainer>
        <Box
          $mr={theme.header.highlighted.margins.afterImage}
          $styles={{width: theme.header.highlighted.imageWidthV2}}
        >
          <Image name="jake" aria-hidden={true} src={HERO_IMAGE} />
        </Box>
        <Box $styles={{width: theme.header.highlighted.textWidthV2}}>
          <ItemDescription>
            <BoldText>Jake Knapp</BoldText>
            &nbsp;is the inventor of <BoldText>the Design Sprint</BoldText> and shared his knowledge
            with Miro users.
          </ItemDescription>
        </Box>
      </ItemContainer>
    </ItemLink>
  )
}

const PopularTemplates = (props: IHighlighted): React.ReactElement => {
  const {title, items, showAllLink} = props
  return (
    <>
      <Headline>{title}</Headline>
      <ul>
        {items.map(item => (
          <li key={item.title}>
            <Item {...item} />
          </li>
        ))}
      </ul>
      <ViewAllLink href={showAllLink.path}>{showAllLink.title}</ViewAllLink>
    </>
  )
}

type HeroBannerHighlightedProps = IHighlighted & {
  treatment: string
}

const HeroBannerHighlighted = (props: HeroBannerHighlightedProps): React.ReactElement => {
  const {treatment, ...rest} = props

  const getContent = useCallback(() => {
    const treatments = {
      v1: (
        <>
          <Headline>Explore community templates</Headline>
          <HeroBannerV1 />
          <ViewAllLink target="_blank" href={MIROVERSE_LINK}>
            View in Miroverse
          </ViewAllLink>
        </>
      ),
      v2: (
        <>
          <Headline>Explore community templates</Headline>
          <HeroBannerV2 />
          <ViewAllLink target="_blank" href={HERO_LINK}>
            View &rsquo;Design Sprint&rsquo; template
          </ViewAllLink>
        </>
      ),
    }

    return treatment && treatments[treatment] ? (
      treatments[treatment]
    ) : (
      <PopularTemplates {...rest} />
    )
  }, [treatment])
  return <Container data-track-place="Hero Banner">{getContent()}</Container>
}

export default Object.assign(HeroBannerHighlighted, {Item: Item})
